import type { AuthenticatedPlatformTransport } from './authenticatedPlatformTransport';

/** Web 只执行已实现的入口；博识空间和未知路由不能通过活动间接打开。 */
export const CAMPAIGN_ROUTES = ['skills', 'scenes', 'connectors', 'settings:account', 'topup'] as const;
export type CampaignRoute = typeof CAMPAIGN_ROUTES[number];
export type CampaignSlot = 'sidebar_card' | 'home_bubble';
export interface CampaignAction {
  type: 'none' | 'claim' | 'open_url' | 'route' | 'page'; label: string;
  url?: string; route?: CampaignRoute; page?: string;
}
export interface CampaignClaimState {
  can_claim: boolean; claimed_today: boolean; claimed_days: number; claimed_total_credit: number; reason: string; next_claim_at?: number;
}
/** 保留平台业务码，跨端重复领取与网络异常分别处理。 */
export class CampaignClaimError extends Error {
  readonly code: string;
  /** code 来自平台，展示层使用本地翻译，不直接渲染服务端错误文本。 */
  constructor(code: string) { super('campaign_unavailable'); this.code = code; }
}
/** 平台公开活动 DTO，只保留展示和用户动作所需字段。奖励单位已是积分，不能再按毫积分转换。 */
export interface CampaignView {
  id: number; slug: string; slot: CampaignSlot; title: string; badge: string; headline: string; subline: string;
  period_no: number; start_at: number; end_at: number; dismissible: boolean; dismiss_scope: 'period' | 'day';
  primary_action: CampaignAction; secondary_action: CampaignAction; page_url: string;
  claim: CampaignClaimState | null; reward_kind?: string; reward_credit?: number;
}
/** 严格读取对象，避免缺失资格被当成可领取。 */
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('campaign_invalid');
  return value as Record<string, unknown>;
}
/** 平台活动字段均为有限非负整数，不接受字符串或金额猜测。 */
function integer(value: unknown): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) throw new Error('campaign_invalid');
  return value as number;
}
/** 说明文字只作为 React 文本渲染，不解析活动 HTML。 */
function text(value: unknown): string { return typeof value === 'string' ? value.slice(0, 4000) : ''; }
/** 校验领取状态，不将缺失 can_claim 解释成 true。 */
function claimState(value: unknown): CampaignClaimState {
  const d = object(value);
  if (typeof d.can_claim !== 'boolean' || typeof d.claimed_today !== 'boolean') throw new Error('campaign_invalid');
  return { can_claim: d.can_claim, claimed_today: d.claimed_today, claimed_days: integer(d.claimed_days),
    claimed_total_credit: integer(d.claimed_total_credit), reason: text(d.reason),
    next_claim_at: d.next_claim_at == null ? undefined : integer(d.next_claim_at) };
}
/** 未实现或危险动作降为不可操作，已支持的另一按钮仍可使用。 */
function action(value: unknown): CampaignAction {
  if (!value) return { type: 'none', label: '' };
  const d = object(value), label = text(d.label);
  if (d.type === 'claim') return { type: 'claim', label };
  if (d.type === 'page') return { type: 'page', label, page: text(d.page) };
  if (d.type === 'open_url') return { type: 'open_url', label, url: text(d.url) };
  if (d.type === 'route' && CAMPAIGN_ROUTES.includes(d.route as CampaignRoute)) return { type: 'route', label, route: d.route as CampaignRoute };
  return { type: 'none', label: '' };
}
/** 活动有效期由平台判断，客户端仅提前隐藏本地已到期卡片。 */
export function isCampaignActive(c: CampaignView, now = Date.now()): boolean {
  return c.start_at <= now / 1000 && (!c.end_at || c.end_at > now / 1000);
}
/** 只允许 HTTP(S) 外链；活动页限定同平台 /events/slug，不携带登录凭据。 */
export function campaignActionUrl(c: CampaignView, a: CampaignAction, platformBase: string): string | null {
  try {
    if (a.type === 'page') {
      const relative = c.page_url || `/events/${a.page || ''}`;
      if (!/^\/events\/[a-zA-Z0-9_-]+$/.test(relative)) return null;
      const base = new URL(platformBase);
      if (!['http:', 'https:'].includes(base.protocol) || base.username || base.password) return null;
      return new URL(relative, base.origin).href;
    }
    if (a.type !== 'open_url' || !a.url) return null;
    const url = new URL(a.url);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}
/** 关闭记录按平台、用户、活动期次隔离；不存令牌或账户余额。 */
export function campaignDismissKey(c: CampaignView, base: string, userId: string, now = new Date()): string {
  const day = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  return `campaign-dismiss:${JSON.stringify([base, userId, c.slug, c.period_no, c.dismiss_scope === 'day' ? day : 'period'])}`;
}
/** 活动与领取复用账户代理，产品固定为 web；平台保留奖励资格、幂等和发放的最终决定权。 */
export function createCampaignClient(transport: AuthenticatedPlatformTransport) {
  let cached: { identity: string; expires: number; slots: Partial<Record<CampaignSlot, CampaignView>> } | null = null;
  const claims = new Map<string, Promise<{ credit: number; claim: CampaignClaimState }>>();
  /** 响应迟到时丢弃；失败不自动重试领取请求。 */
  async function request(path: string, body?: Record<string, unknown>) {
    const identity = transport.identity();
    if (!identity) throw new Error('unauthenticated');
    const current = () => identity === transport.identity();
    const result = await transport.request(path, current, body);
    if (!current()) throw new Error('superseded');
    if (result.status < 200 || result.status >= 300) throw new Error('campaign_unavailable');
    if (!result.success) {
      const payload = result.data && typeof result.data === 'object' ? result.data as Record<string, unknown> : {};
      throw new CampaignClaimError(text(payload.code));
    }
    return result.data;
  }
  return {
    /** 首页与侧栏共享短时缓存；显式刷新或领取后重新请求平台。 */
    async active(force = false): Promise<Partial<Record<CampaignSlot, CampaignView>>> {
      const identity = transport.identity();
      if (!identity) { cached = null; return {}; }
      if (!force && cached?.identity === identity && cached.expires > Date.now()) return cached.slots;
      const data = object(await request('/api/campaign/active?platform=web'));
      if (identity !== transport.identity()) throw new Error('superseded');
      const slots: Partial<Record<CampaignSlot, CampaignView>> = {};
      if (data.enabled === true) {
        const raw = object(data.slots);
        for (const slot of ['sidebar_card', 'home_bubble'] as const) {
          if (!raw[slot]) continue;
          const d = object(raw[slot]);
          if (d.slot !== slot || !text(d.slug) || integer(d.id) === 0) throw new Error('campaign_invalid');
          const card: CampaignView = { id: integer(d.id), slug: text(d.slug), slot, title: text(d.title), badge: text(d.badge),
            headline: text(d.headline), subline: text(d.subline).replace(/\{\{\s*reward\s*\}\}/g, String(integer(d.reward_credit ?? 0))),
            reward_kind: text(d.reward_kind), reward_credit: integer(d.reward_credit ?? 0), period_no: integer(d.period_no), start_at: integer(d.start_at), end_at: integer(d.end_at),
            dismissible: d.dismissible === true, dismiss_scope: d.dismiss_scope === 'day' ? 'day' : 'period',
            primary_action: action(d.primary_action), secondary_action: action(d.secondary_action), page_url: text(d.page_url), claim: d.claim ? claimState(d.claim) : null };
          if (isCampaignActive(card)) slots[slot] = card;
        }
      }
      cached = { identity, expires: Date.now() + 30_000, slots };
      return slots;
    },
    /** 重复点击共享同一个请求；超时后 UI 刷新资格，不自行补发奖励。 */
    claim(id: number): Promise<{ credit: number; claim: CampaignClaimState }> {
      if (integer(id) === 0) return Promise.reject(new Error('campaign_invalid'));
      const key = JSON.stringify([transport.identity(), id]);
      const existing = claims.get(key);
      if (existing) return existing;
      const pending = request(`/api/campaign/${id}/claim?platform=web`, {}).then(value => {
        const d = object(value);
        return { credit: integer(d.credit), claim: claimState(d.claim) };
      }).finally(() => { cached = null; claims.delete(key); });
      claims.set(key, pending);
      return pending;
    },
  };
}
